import type { CSSProperties } from "react";

/**
 * Motyw kolorystyczny per gmina — dowolny kolor HEX zapisywany w
 * tenant.payload.primaryColor. Stosujemy przez style inline (nie klasy
 * Tailwind), bo Tailwind nie potrafi wygenerować klas z dynamicznej
 * wartości. Funkcja getThemeStyles zwraca gotowe `style={{...}}` dla
 * każdego elementu strony publicznej.
 */

/** Domyślny kolor motywu — Tailwind blue-700. */
export const DEFAULT_PRIMARY_COLOR = "#1d4ed8";

/** Propozycje kolorów wyświetlane jako swatche w color pickerze. */
export const PRESET_COLORS: Array<{ hex: string; label: string }> = [
  { hex: "#1d4ed8", label: "Niebieski" },
  { hex: "#334155", label: "Grafitowy" },
  { hex: "#0f766e", label: "Morski" },
  { hex: "#047857", label: "Zielony" },
  { hex: "#b45309", label: "Bursztynowy" },
  { hex: "#b91c1c", label: "Czerwony" },
  { hex: "#4338ca", label: "Indygo" },
  { hex: "#6d28d9", label: "Fioletowy" },
  { hex: "#0891b2", label: "Cyjan" },
  { hex: "#be185d", label: "Różowy" },
  { hex: "#000000", label: "Czarny" },
];

export type ThemeStyles = {
  header: CSSProperties;
  headerSubtitle: CSSProperties;
  sectionTitle: CSSProperties;
  accentText: CSSProperties;
  selectedItem: CSSProperties;
  selectedSlot: CSSProperties;
};

/** Parsuje "#rrggbb" do {r,g,b}. Tolerantny — w razie błędu zwraca czarny. */
function parseHex(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return { r: 0, g: 0, b: 0 };
  return { r, g, b };
}

/** Zwraca "rgba(r,g,b,alpha)" dla danego hex. */
function hexToRgba(hex: string, alpha: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Automatycznie dobiera kolor tekstu na tle: biały dla ciemnych kolorów,
 * czarny dla jasnych. Próg luminancji 0.55 (WCAG-owy kompromis czytelności).
 */
function readableTextOn(hex: string): "#ffffff" | "#0f172a" {
  const { r, g, b } = parseHex(hex);
  // Relative luminance wg W3C (uproszczone — nie gamma-correct, ale wystarczy).
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.6 ? "#0f172a" : "#ffffff";
}

/** Zwraca ciemniejszą wersję koloru (mnożnik 0..1, 0.7 = 30% ciemniej). */
function darken(hex: string, factor: number): string {
  const { r, g, b } = parseHex(hex);
  const d = (n: number) => Math.max(0, Math.min(255, Math.round(n * factor)));
  return `rgb(${d(r)}, ${d(g)}, ${d(b)})`;
}

/**
 * Zwraca zbiór `style={{...}}` dla pozostałych miejsc używających motywu.
 * Wszystkie wariacje są obliczane z jednego koloru HEX.
 */
export function getThemeStyles(color: string | undefined | null): ThemeStyles {
  const c = (color && color.trim()) || DEFAULT_PRIMARY_COLOR;
  const textColor = readableTextOn(c);
  const subtitleAlpha = textColor === "#ffffff" ? 0.8 : 0.65;

  return {
    header: {
      backgroundColor: c,
      color: textColor,
    },
    headerSubtitle: {
      color:
        textColor === "#ffffff"
          ? `rgba(255, 255, 255, ${subtitleAlpha})`
          : `rgba(15, 23, 42, ${subtitleAlpha})`,
    },
    // Nagłówki sekcji w panelu — ciemniejsza wersja koloru, żeby nie konkurowały
    // z headerem i pozostały czytelne na białym tle.
    sectionTitle: {
      color: darken(c, 0.75),
    },
    // Akcent tekstowy (np. wybrana godzina).
    accentText: {
      color: c,
    },
    // Wybrana sesja konsultacji — delikatne tło + pełna ramka w kolorze.
    selectedItem: {
      borderColor: c,
      backgroundColor: hexToRgba(c, 0.08),
    },
    // Aktywny slot terminu — pełny kolor tła + kontrastowy tekst.
    selectedSlot: {
      backgroundColor: c,
      color: textColor,
    },
  };
}

/** Prosta walidacja hex: #rgb, #rrggbb. */
export function isValidHexColor(value: string): boolean {
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());
}
